import { useLocalization } from "@fluent/react";
import { LoadingShim } from "@narmi/design_system";
import { useMemo } from "react";
import { ErrorScreen } from "@lending/components/ErrorScreen/ErrorScreen";
import { ApplicationCard } from "./components/ApplicationCard";
import { AttentionCard } from "./components/AttentionCard";
import styles from "./LoanPortalDashboard.module.css";
import { needsAttention } from "./portalApplicationStatus";
import { useGetAccessibleApplicationsQuery } from "./useGetAccessibleApplicationsQuery";
import type { LoanPortalTab } from "./LoanPortalLayout";

export function LoanPortalDashboard({
  activeTab = "applications",
  onTabChange,
}: {
  activeTab?: LoanPortalTab;
  onTabChange?: (tab: LoanPortalTab) => void;
}) {
  const { l10n } = useLocalization();
  const { data: applications, isLoading, isError } = useGetAccessibleApplicationsQuery();

  const attentionItems = useMemo(() => (applications ?? []).filter(needsAttention), [applications]);

  if (isError) {
    return <ErrorScreen />;
  }

  const hasApplications = (applications?.length ?? 0) > 0;
  const firstName = applications?.[0]?.firstName;
  const headerLocalizationArgs = firstName ? { firstName } : null;
  const fallback = firstName ? `Welcome back, ${firstName}` : "Welcome back";

  return (
    <LoadingShim isLoading={isLoading}>
      <div className={`nds-typography ${styles.container}`}>
        <div className={styles.headingGroup}>
          <div className={styles.subheading}>
            {l10n.getString("loan-portal-nav-title", null, "My loan applications")}
          </div>
          <h1 className={styles.heading}>
            <span className={styles.welcomeWave} aria-hidden="true">
              👋
            </span>
            {firstName
              ? l10n.getString("loan-portal-welcome", headerLocalizationArgs, fallback)
              : l10n.getString("loan-portal-welcome-no-firstname", null, "Welcome back")}
          </h1>
        </div>
        <div className={styles.dashboardBody}>
          {!isLoading && !hasApplications && activeTab === "applications" && (
            <p className={styles.empty}>
              {l10n.getString(
                "loan-portal-empty",
                null,
                "You don't have any loan applications yet."
              )}
            </p>
          )}

          <AttentionCard applications={attentionItems} />

          <div className={styles.mobileTabs}>
            <button
              type="button"
              className={activeTab === "applications" ? styles.activeTab : styles.tab}
              aria-pressed={activeTab === "applications"}
              onClick={() => onTabChange?.("applications")}
            >
              {l10n.getString("loan-portal-your-applications", null, "Applications")}
            </button>
            <button
              type="button"
              className={activeTab === "services" ? styles.activeTab : styles.tab}
              aria-pressed={activeTab === "services"}
              onClick={() => onTabChange?.("services")}
            >
              {l10n.getString("loan-portal-explore-services", null, "Explore services")}
            </button>
          </div>

          {hasApplications && activeTab === "applications" && (
            <div
              role="region"
              aria-label={l10n.getString(
                "loan-portal-your-applications",
                null,
                "Your applications"
              )}
              className={styles.applicationsSection}
            >
              <h2 className={styles.sectionTitle}>
                {l10n.getString("loan-portal-your-applications", null, "Your applications")}
              </h2>
              <div className={styles.applicationsGrid}>
                {(applications ?? []).map((application) => (
                  <ApplicationCard key={application.uuid} application={application} />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </LoadingShim>
  );
}
